import { useMemo } from 'react';
import type { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import map from 'lodash/map';
import Points from './Points';
import Text from '../components/atoms/Text';
import ImagePreview from './ImagePreview';
import { getPersonas } from '../data/Personas';
import { findConceptByName } from '../data/Concepts';

const Persona = styled.div`
	background-color: ${(props) => props.theme.colors.white};
	border-radius: 1em;
	margin: 1em;
	padding: 0.25em 1em;
	& h2,
	h3 {
		color: ${(props) => props.theme.colors.darkBlue};
	}
	& dt {
		font-weight: bold;
	}
	& dt:after {
		content: ':';
	}
	& dd {
		margin-bottom: 0.5em;
	}
`;

interface Props {}

const PersonaList: FunctionComponent<Props> = () => {
	const { t } = useTranslation();

	const personaItems = useMemo(() => {
		return map(getPersonas(), (persona) => {
			const needs: string[] = t(persona.texts.shortDescription, {
				returnObjects: true
			});
			const reactions = map(persona.reactions, (reaction, index) => {
				const concept = findConceptByName(reaction.conceptName);
				return (
					<tr key={index}>
						<td>
							{concept
								? t(concept.texts.name)
								: `Concept '${reaction.conceptName}' not found!`}
						</td>
						<td align="right">
							<Points value={reaction.points} />
						</td>
						<td>
							<Trans>{reaction.reaction}</Trans>
						</td>
					</tr>
				);
			});
			return (
				<Persona key={persona.name}>
					<h2>{persona.name}</h2>
					<h3>
						<Trans>data.images</Trans>
					</h3>
					<table>
						<thead>
							<tr>
								<th></th>
								<th>Negative</th>
								<th>Positive</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<th>Body</th>
								<td>
									<ImagePreview imageKey={persona.images.body.negative} />
								</td>
								<td>
									<ImagePreview imageKey={persona.images.body.positive} />
								</td>
							</tr>
							<tr>
								<th>Face</th>
								<td>
									<ImagePreview imageKey={persona.images.face.negative} />
								</td>
								<td>
									<ImagePreview imageKey={persona.images.face.positive} />
								</td>
							</tr>
						</tbody>
					</table>
					<h3>
						<Trans>data.texts</Trans>
					</h3>
					<dl>
						<dt>Name</dt>
						<dd>
							<Trans>{persona.texts.name}</Trans>
						</dd>
						<dt>Title</dt>
						<dd>
							<Text content={t(persona.texts.title)} />
						</dd>
						<dt>Short Description</dt>
						<dd>
							{map(needs, (need, index) => (
								<li key={index}>{need}</li>
							))}
						</dd>
						<dt>Description</dt>
						<dd>
							<Text
								content={t(persona.texts.description, {
									returnObjects: true
								})}
							/>
						</dd>
					</dl>
					<h3>
						<Trans>data.reactions</Trans>
					</h3>
					<table>
						<thead>
							<tr>
								<th>Concept</th>
								<th>Points</th>
								<th>Text</th>
							</tr>
						</thead>
						<tbody>{reactions}</tbody>
					</table>
				</Persona>
			);
		});
	}, [t]);

	return <>{personaItems}</>;
};

export default PersonaList;
